import React, { useState, useEffect, useMemo } from 'react';
import compensacionService from '../services/compensacionService';
import ImportadorCompensaciones from './ImportadorCompensaciones';
import importacionCompensacionesService from '../services/importacionCompensacionesService';

const CompensacionesModal = ({ isOpen, onClose, planillaId, planillaNombre, onRecalcularPlanilla, empleadosActivos = [] }) => {
  const [compensaciones, setCompensaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editedHoras, setEditedHoras] = useState({});
  const [totalHoras, setTotalHoras] = useState(0);
  
  const idsEmpleadosEditables = useMemo(() => {
    if (!empleadosActivos?.length) return null;
    return new Set(
      empleadosActivos
        .map((e) => Number(e.id ?? e.empleado_id))
        .filter((id) => Number.isFinite(id) && id > 0),
    );
  }, [empleadosActivos]);

  const compensacionesEditables = useMemo(() => {
    if (!idsEmpleadosEditables) return compensaciones;
    return compensaciones.filter((c) => idsEmpleadosEditables.has(Number(c.empleado_id)));
  }, [compensaciones, idsEmpleadosEditables]);
  
  // Estados para el importador
  const [showImportador, setShowImportador] = useState(false);
  const [datosImportados, setDatosImportados] = useState(null);

  // Cargar compensaciones al abrir el modal
  useEffect(() => {
    if (isOpen && planillaId) {
      cargarCompensaciones();
    }
  }, [isOpen, planillaId]);

  // Calcular total de horas cuando cambien las compensaciones o ediciones
  useEffect(() => {
    calcularTotalHoras();
  }, [compensacionesEditables, editedHoras]);

  const cargarCompensaciones = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log(`📊 Cargando compensaciones para planilla ${planillaId}`);
      
      const resultado = await compensacionService.obtenerCompensacionesPorPlanilla(planillaId);
      
      if (resultado.success) {
        setCompensaciones(resultado.data);
        console.log(`✅ Compensaciones cargadas: ${resultado.total} registros`);
        
        // Inicializar horas editadas con valores actuales
        const horasIniciales = {};
        resultado.data.forEach(comp => {
          horasIniciales[comp.id] = comp.horas_compensadas;
        });
        setEditedHoras(horasIniciales);
        
      } else {
        setError(resultado.error || 'Error al cargar compensaciones');
      }
    } catch (error) {
      console.error('❌ Error inesperado:', error);
      setError('Error de conexión al cargar compensaciones');
    } finally {
      setLoading(false);
    }
  };

  const calcularTotalHoras = () => {
    const total = compensacionesEditables.reduce((sum, comp) => {
      const horas = editedHoras[comp.id] || comp.horas_compensadas || 0;
      return sum + parseFloat(horas);
    }, 0);
    setTotalHoras(total);
  };

  const handleHorasChange = (compensacionId, nuevasHoras) => {
    // Permitir valores vacíos o "-" mientras el usuario escribe
    if (nuevasHoras === '' || nuevasHoras === '-') {
      setEditedHoras(prev => ({
        ...prev,
        [compensacionId]: nuevasHoras === '' ? 0 : nuevasHoras
      }));
      return;
    }
    
    // Convertir formato HH:MM (base 60) a decimal (base 100)
    const horasDecimales = horasMinutosADecimal(nuevasHoras);
    
    // Guardar en decimal (base 100) internamente
    setEditedHoras(prev => ({
      ...prev,
      [compensacionId]: horasDecimales
    }));
  };

  const guardarCambios = async () => {
    setSaving(true);
    setError(null);
    
    try {
      console.log('💾 Guardando cambios en compensaciones...');
      
      // Encontrar compensaciones que han cambiado
      const cambios = compensacionesEditables.filter(comp => {
        const horasOriginales = comp.horas_compensadas;
        const horasEditadas = editedHoras[comp.id];
        return horasEditadas !== horasOriginales;
      });
      
      if (cambios.length === 0) {
        console.log('ℹ️ No hay cambios para guardar');
        alert('ℹ️ No se detectaron cambios en las compensaciones');
        onClose();
        return;
      }
      
      console.log(`🔄 Actualizando ${cambios.length} compensaciones...`);
      
      // Actualizar cada compensación que cambió
      const promises = cambios.map(comp => 
        compensacionService.actualizarHorasCompensadas(comp.id, editedHoras[comp.id])
      );
      
      const resultados = await Promise.all(promises);
      
      // Verificar si todos fueron exitosos
      const exitosos = resultados.filter(r => r.success).length;
      const errores = resultados.filter(r => !r.success);
      
      if (errores.length > 0) {
        console.error('❌ Algunos cambios fallaron:', errores);
        setError(`Error al actualizar ${errores.length} compensaciones`);
        return;
      }
      
      console.log(`✅ ${exitosos} compensaciones actualizadas correctamente`);
      
      // Recargar datos para mostrar cambios guardados
      await cargarCompensaciones();
      
      // Mostrar mensaje de éxito
      console.log(`🔄 Ejecutando recálculo automático de la planilla...`);
      
      // Ejecutar recálculo de planilla si se proporcionó la función
      if (onRecalcularPlanilla && typeof onRecalcularPlanilla === 'function') {
        try {
          // Cerrar modal antes del recálculo para mejor UX
          onClose();
          
          // Ejecutar recálculo de planilla
          await onRecalcularPlanilla();
          
          console.log(`✅ Compensaciones guardadas y planilla recalculada exitosamente`);
          
          // Notification de éxito
          setTimeout(() => {
            alert(`🎯 ¡Completado exitosamente!\n\n✅ ${exitosos} compensaciones actualizadas\n⚡ Planilla recalculada automáticamente\n\nLos nuevos cálculos ya están disponibles en todas las vistas.`);
          }, 500);
          
        } catch (errorRecalculo) {
          console.error('❌ Error al recalcular planilla:', errorRecalculo);
          alert(`✅ Compensaciones guardadas correctamente, pero hubo un error al recalcular la planilla: ${errorRecalculo.message}`);
        }
      } else {
        // Si no hay función de recálculo, solo mostrar mensaje y cerrar
        alert(`✅ Se actualizaron ${exitosos} compensaciones correctamente`);
        onClose();
      }
      
    } catch (error) {
      console.error('❌ Error al guardar cambios:', error);
      setError('Error inesperado al guardar cambios');
    } finally {
      setSaving(false);
    }
  };

  const resetearCambios = () => {
    // Restaurar valores originales
    const horasOriginales = {};
    compensaciones.forEach(comp => {
      horasOriginales[comp.id] = comp.horas_compensadas;
    });
    setEditedHoras(horasOriginales);
  };

  // ⏰ FUNCIÓN PARA CONVERTIR DECIMAL (BASE 100) A FORMATO HH:MM (BASE 60)
  // Ejemplos: 8.5 → "8:30" | 7.75 → "7:45" | -2.25 → "-2:15"
  const decimalAHorasMinutos = (horasDecimales) => {
    if (!horasDecimales && horasDecimales !== 0) return '0:00';
    
    const esNegativo = horasDecimales < 0;
    const horasDecimalesAbs = Math.abs(horasDecimales);
    
    const horas = Math.floor(horasDecimalesAbs);
    const minutos = Math.round((horasDecimalesAbs - horas) * 60);
    
    // Asegurar que los minutos sean de 2 dígitos
    const minutosStr = minutos.toString().padStart(2, '0');
    
    return `${esNegativo ? '-' : ''}${horas}:${minutosStr}`;
  };

  // ⏰ FUNCIÓN PARA CONVERTIR FORMATO HH:MM (BASE 60) A DECIMAL (BASE 100)
  // Ejemplos: "8:30" → 8.5 | "7:45" → 7.75 | "-2:15" → -2.25
  const horasMinutosADecimal = (horasMinutosStr) => {
    if (!horasMinutosStr || horasMinutosStr === '') return 0;
    
    // Manejar valores negativos
    const esNegativo = horasMinutosStr.startsWith('-');
    const strSinSigno = esNegativo ? horasMinutosStr.substring(1) : horasMinutosStr;
    
    // Validar formato HH:MM o HH:MM:SS
    const partes = strSinSigno.split(':');
    if (partes.length < 2) {
      // Si no tiene formato HH:MM, intentar parsear como decimal
      const decimal = parseFloat(horasMinutosStr);
      return isNaN(decimal) ? 0 : decimal;
    }
    
    const horas = parseInt(partes[0]) || 0;
    const minutos = parseInt(partes[1]) || 0;
    
    // Convertir minutos a fracción decimal de hora
    const horasDecimales = horas + (minutos / 60);
    
    return esNegativo ? -horasDecimales : horasDecimales;
  };

  // 📊 Formatear horas para mostrar (mantener para compatibilidad)
  const formatearHoras = (horas) => {
    return decimalAHorasMinutos(horas);
  };

  // 📥 Manejar datos importados del componente ImportadorCompensaciones
  const manejarDatosImportados = async (datosImportacion) => {
    try {
      console.log('📥 Datos importados recibidos:', datosImportacion);
      
      // Guardar los datos importados para mostrar en la interfaz
      setDatosImportados(datosImportacion);
      
      // Aplicar los datos importados a las compensaciones existentes
      const horasActualizadas = { ...editedHoras };
      
      // Buscar coincidencias por nombre de empleado
      datosImportacion.datos.forEach(datoImportado => {
        const compensacionCoincidente = compensacionesEditables.find(comp => {
          const nombreEmpleado = comp.empleado?.nombre_completo || '';
          return nombreEmpleado.toLowerCase().includes(datoImportado.empleado.toLowerCase()) ||
                 datoImportado.empleado.toLowerCase().includes(nombreEmpleado.toLowerCase());
        });
        
        if (compensacionCoincidente) {
          horasActualizadas[compensacionCoincidente.id] = datoImportado.horasProximoMes;
          console.log(`✅ Coincidencia encontrada: ${datoImportado.empleado} -> ${datoImportado.horasProximoMes}h`);
        } else {
          console.warn(`⚠️ No se encontró coincidencia para: ${datoImportado.empleado}`);
        }
      });
      
      // Actualizar las horas editadas
      setEditedHoras(horasActualizadas);
      
      // Cerrar el importador
      setShowImportador(false);
      
      // Mostrar resumen de importación
      const coincidenciasEncontradas = datosImportacion.datos.filter(dato => {
        return compensacionesEditables.some(comp => {
          const nombreEmpleado = comp.empleado?.nombre_completo || '';
          return nombreEmpleado.toLowerCase().includes(dato.empleado.toLowerCase()) ||
                 dato.empleado.toLowerCase().includes(nombreEmpleado.toLowerCase());
        });
      }).length;
      
      alert(`📊 Importación completada\n\n` +
            `✅ ${coincidenciasEncontradas} empleados encontrados y actualizados\n` +
            `⚠️ ${datosImportacion.datos.length - coincidenciasEncontradas} empleados no encontraron coincidencia\n\n` +
            `💡 Revise los datos antes de guardar los cambios.`);
      
    } catch (error) {
      console.error('❌ Error procesando datos importados:', error);
      setError('Error al procesar los datos importados: ' + error.message);
    }
  };

  // 📂 Abrir importador
  const abrirImportador = () => {
    setShowImportador(true);
    setError(null);
  };

  // ❌ Cerrar importador
  const cerrarImportador = () => {
    setShowImportador(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
        
        {/* Header */}
        <div className="bg-orange-500 text-white p-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">📅 Compensaciones de Meses Pasados</h2>
              <p className="text-orange-100 text-sm mt-1">
                Planilla: {planillaNombre} | Total empleados: {compensacionesEditables.length}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              {/* Botón Importar Datos */}
              {!loading && compensacionesEditables.length > 0 && (
                <button
                  onClick={abrirImportador}
                  className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium"
                  title="Importar datos de compensaciones desde Excel o CSV"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span>📥 Importar Datos</span>
                </button>
              )}
              
              <button
                onClick={onClose}
                className="text-white hover:text-orange-200 transition-colors"
              >
                <span className="text-2xl">×</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 overflow-y-auto max-h-[calc(90vh-200px)]">
          
          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-center space-x-2">
                <span className="text-red-500">❌</span>
                <span className="text-red-700 font-medium">Error</span>
              </div>
              <p className="text-red-600 text-sm mt-1">{error}</p>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              <p className="text-gray-600 mt-2">Cargando compensaciones...</p>
            </div>
          )}

          {/* Compensaciones List */}
          {!loading && compensacionesEditables.length > 0 && (
            <>
              {/* Info Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
                <div className="flex items-start space-x-3">
                  <span className="text-blue-500 text-xl">ℹ️</span>
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-1">Instrucciones</h4>
                    <p className="text-blue-700 text-sm">
                      Ingrese las horas de compensación pendientes de meses anteriores para cada empleado. 
                      Estas horas se añadirán como conceptos adicionales en la planilla actual.
                      {onRecalcularPlanilla && (
                        <span className="block mt-2 font-medium">
                          ⚡ Al guardar, se recalculará automáticamente toda la planilla con las nuevas compensaciones.
                        </span>
                      )}
                    </p>
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <p className="text-blue-600 text-xs">
                        💡 <strong>Consejo:</strong> Use el botón "📥 Importar Datos" para cargar horas desde Excel/CSV con las columnas: 
                        Empleado, Total Próximo Mes (h), Cargo, Área, Proyecto, Estado.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Banner de Datos Importados */}
              {datosImportados && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
                  <div className="flex items-start space-x-3">
                    <span className="text-green-500 text-xl">📊</span>
                    <div>
                      <h4 className="font-semibold text-green-800 mb-1">Datos Importados</h4>
                      <p className="text-green-700 text-sm mb-2">
                        Archivo: <strong>{datosImportados.archivoOriginal}</strong> | 
                        Registros procesados: <strong>{datosImportados.resumen.totalRegistros}</strong> | 
                        Total horas: <strong>{datosImportados.resumen.totalHoras || datosImportados.resumen.totalHorasProximoMes}h</strong>
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-green-600">
                        <div>
                          <span className="font-medium">Empleados únicos:</span> {datosImportados.resumen.empleadosUnicos}
                        </div>
                        <div>
                          <span className="font-medium">Áreas únicas:</span> {datosImportados.resumen.areasUnicas}
                        </div>
                        <div>
                          <span className="font-medium">Proyectos únicos:</span> {datosImportados.resumen.proyectosUnicos}
                        </div>
                        <div>
                          <span className="font-medium">Promedio horas:</span> {datosImportados.resumen.promedioHorasPorEmpleado}h
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Summary */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 mb-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{compensacionesEditables.length}</div>
                    <div className="text-orange-700 text-sm font-medium">Total Empleados</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{decimalAHorasMinutos(totalHoras)}h</div>
                    <div className="text-orange-700 text-sm font-medium">Total Horas</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {compensacionesEditables.filter(c => (editedHoras[c.id] || c.horas_compensadas) > 0).length}
                    </div>
                    <div className="text-orange-700 text-sm font-medium">Con Compensaciones</div>
                  </div>
                </div>
                
                {/* Indicador de recálculo automático */}
                {onRecalcularPlanilla && totalHoras > 0 && (
                  <div className="mt-4 pt-4 border-t border-orange-200">
                    <div className="flex items-center justify-center space-x-2 text-orange-800">
                      <span>⚡</span>
                      <span className="text-sm font-medium">
                        Al guardar se recalculará automáticamente la planilla completa
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Table */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider w-1/3">
                          Empleado
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider w-1/5">
                          Cargo
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider w-1/5">
                          Proyecto
                        </th>
                        <th className="px-6 py-4 text-center text-sm font-medium text-gray-500 uppercase tracking-wider w-1/6">
                          Horas Actuales
                        </th>
                        <th className="px-6 py-4 text-center text-sm font-medium text-gray-500 uppercase tracking-wider w-1/6">
                          Nuevas Horas
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {compensacionesEditables.map((compensacion) => (
                        <tr key={compensacion.id} className="hover:bg-gray-50">
                          <td className="px-6 py-5">
                            <div>
                              <div className="text-sm font-semibold text-gray-900">
                                {compensacion.empleado?.nombre_completo || 'Sin nombre'}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                ID: {compensacion.empleado_id}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            <div className="text-sm text-gray-900 break-words">
                              {compensacion.empleado?.cargo || 'Sin cargo'}
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            <div className="text-sm text-gray-900 break-words">
                              {compensacion.empleado?.proyecto || 'Sin proyecto'}
                            </div>
                          </td>
                          <td className="px-6 py-5 text-center">
                            <div className="bg-gray-50 rounded-lg px-3 py-2 inline-block">
                              <span className="text-sm font-medium text-gray-700">
                                {decimalAHorasMinutos(compensacion.horas_compensadas)}h
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-5 text-center">
                            <div className="flex items-center justify-center space-x-2">
                              <input
                                type="text"
                                value={editedHoras[compensacion.id] !== undefined 
                                  ? decimalAHorasMinutos(editedHoras[compensacion.id]) 
                                  : decimalAHorasMinutos(compensacion.horas_compensadas || 0)}
                                onChange={(e) => handleHorasChange(compensacion.id, e.target.value)}
                                className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-center text-sm font-medium focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
                                placeholder="0:00"
                                pattern="^-?\d{1,3}:\d{2}$"
                                title="Formato: HH:MM (ejemplo: 8:30 o -2:15)"
                              />
                              <span className="text-sm text-gray-500 font-medium">h</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Empty State */}
          {!loading && compensaciones.length === 0 && (
            <div className="text-center py-12">
              <span className="text-6xl">📭</span>
              <h3 className="text-lg font-medium text-gray-900 mt-4">No hay compensaciones</h3>
              <p className="text-gray-600 mt-2">
                No se encontraron registros de compensaciones para esta planilla.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && compensaciones.length > 0 && (
          <div className="bg-gray-50 px-8 py-5 flex items-center justify-between border-t border-gray-200">
            <div className="flex items-center space-x-4">
              <button
                onClick={resetearCambios}
                className="text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                🔄 Resetear cambios
              </button>
              <div className="text-sm text-gray-600">
                Total: <span className="font-semibold text-orange-600">{decimalAHorasMinutos(totalHoras)}h</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={guardarCambios}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-500 border border-transparent rounded-md hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? (
                  <span className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>{onRecalcularPlanilla ? 'Guardando y recalculando...' : 'Guardando...'}</span>
                  </span>
                ) : (
                  onRecalcularPlanilla ? '💾 Guardar y Recalcular Planilla' : '💾 Guardar Cambios'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Modal del Importador */}
        {showImportador && (
          <ImportadorCompensaciones
            onDatosImportados={manejarDatosImportados}
            onClose={cerrarImportador}
            empleadosActivos={empleadosActivos}
          />
        )}
      </div>
    </div>
  );
};

export default CompensacionesModal; 